import { makeAutoObservable, observable } from "mobx";

import type { Team, TeamShort, Folder, Document } from "@/api";

type CacheObject = Team | TeamShort | Folder | Document;
type Uid = CacheObject["uid"];
export type CacheTypename = CacheObject["typename"];

type EntityShapes = { [K in CacheTypename]?: Extract<CacheObject, { typename: K }> };

/*
 * Normalized api cache. Outer Map is keyed by uid; inner object is keyed by
 * typename, so different shapes of the same entity (e.g. TeamShort vs Team)
 * coexist as sibling entries.
 *
 * This cache is mobx observable, so all changes are reflected everywhere it is used.
 *
 * TODO: guard against ssr use
 * TODO: garbage collection
 */
class ApiCacheStore {
  objects: Map<Uid, EntityShapes> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  get<T extends CacheObject>(typename: T["typename"], uid: Uid): T | undefined {
    return this.objects.get(uid)?.[typename] as T | undefined;
  }

  delete(typename: CacheTypename, uid: Uid): void {
    const shapes = this.objects.get(uid);
    if (!shapes) return;
    delete shapes[typename];
    if (Object.keys(shapes).length === 0) {
      this.objects.delete(uid);
    }
  }

  clear(): void {
    this.objects.clear();
  }

  /**
   * Updates the key of an object in the cache without breaking MobX observability.
   * This is useful when the UID changes (e.g., after creating an object optimistically).
   */
  updateKey(oldUid: Uid, newUid: Uid): boolean {
    const shapes = this.objects.get(oldUid);
    if (shapes) {
      for (const obj of Object.values(shapes)) {
        Object.assign(obj, { uid: newUid });
      }
      this.objects.delete(oldUid);
      this.objects.set(newUid, shapes);
      return true;
    }
    return false;
  }

  /**
   * Set a root object and recursively cache all nested objects, replacing nested objects with
   * reference to the cache. Obects that already cached are used as reference and updated if
   * the new object is changed.
   *
   * Not cacheable objects that don't have uid/typename, are not stored and returned as is.
   *
   * This function is called for all API request results, you likely don't need to access
   * it directly.
   */
  set<T>(obj: T): T {
    if (this.isArray(obj)) {
      return obj.map((item) => this.set(item)) as T;
    }
    if (this.isPlainObject(obj)) {
      const patchedObj = Object.keys(obj).reduce(
        (result, key) => {
          // Filter out null userPermissions when updating an existing object
          // TODO: remove after https://linear.app/corca-research/issue/D-1563/dont-add-userpermissions-field-in-serializer-if-it-is-null
          if (key === "userPermissions" && obj[key] === null) {
            return result;
          }
          result[key] = this.set(obj[key]);
          return result;
        },
        {} as Record<string, unknown>,
      ) as T;

      if (this.isCacheObject(patchedObj)) {
        let shapes = this.objects.get(patchedObj.uid);
        if (!shapes) {
          shapes = observable({} as EntityShapes);
          this.objects.set(patchedObj.uid, shapes);
        }
        const existing = shapes[patchedObj.typename];
        if (existing) {
          Object.assign(existing, patchedObj);
        } else {
          (shapes as Record<CacheTypename, CacheObject>)[patchedObj.typename] =
            observable(patchedObj);
        }
        this.mergeSiblings(patchedObj);

        return shapes[patchedObj.typename] as T;
      }
      return patchedObj;
    }

    return obj;
  }

  /**
   * Cross-shape propagation: an update to one shape (e.g. Team) needs to
   * be visible on its sibling shapes (e.g. TeamShort) for the same uid.
   */
  private mergeSiblings(updated: CacheObject): void {
    const shapes = this.objects.get(updated.uid);
    if (!shapes) return;
    for (const t of Object.keys(shapes) as CacheTypename[]) {
      if (t === updated.typename) continue;
      const sibling = shapes[t];
      if (!sibling) continue;
      for (const [field, value] of Object.entries(updated)) {
        if (field === "typename" || !(field in sibling)) continue;
        (sibling as unknown as Record<string, unknown>)[field] = value;
      }
    }
  }

  private isCacheObject(value: unknown): value is CacheObject {
    return typeof value === "object" && value !== null && "uid" in value && "typename" in value;
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    if (value === null || typeof value !== "object") return false;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
  }

  private isArray(value: unknown): value is Array<unknown> {
    return Array.isArray(value);
  }
}

export const apiCache = new ApiCacheStore();
